/* Talous ja laskutus widgets (foreman and admin only). */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ErrorIcon from '@mui/icons-material/Error';
import EuroIcon from '@mui/icons-material/Euro';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

import { dataSources } from '../dataSources';
import { useDashboardActions } from '../dashboardActions';
import BreakdownTile from '../templates/BreakdownTile';
import ChartBody from '../templates/ChartBody';
import KpiGroupBody from '../templates/KpiGroupBody';
import KpiTile from '../templates/KpiTile';
import ListBody from '../templates/ListBody';
import { addDaysIso, daysUntil, fmtEur0, fmtEur2, fmtNumber, initials, todayIso } from '../templates/format';
import { listOf, useHeader, useSource } from '../widgetHooks';

const trendOf = (value) => (value ? { text: `${value > 0 ? '+' : ''}${value} %`, up: value >= 0 } : null);
const changeOf = (kpi) => (kpi?.change_percent != null && kpi.change_percent !== 0 ? { text: `${kpi.change_percent > 0 ? '+' : ''}${Math.round(kpi.change_percent)} %`, down: kpi.change_type === 'decrease' || kpi.change_percent < 0 } : null);

export function RevenueTile() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.stats());
  return <KpiTile icon={EuroIcon} value={fmtEur0(data?.monthly_revenue ?? 0)} label={t('dashboard.widgets.revenue.title')} sub={t('dashboard.chips.exclVat')} trend={trendOf(data?.monthly_revenue_trend)} />;
}

export function ReceivablesTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.stats());
  const overdue = data?.overdue_invoices_count ?? 0;
  return (
    <KpiTile
      icon={AccountBalanceWalletIcon}
      value={fmtEur0(data?.unpaid_invoices_total ?? 0)}
      label={t('dashboard.widgets.receivables.title')}
      sub={overdue > 0 ? `${t('dashboard.chips.overdueCount', { count: overdue })} · ${fmtEur0(data?.overdue_invoices_total ?? 0)}` : t('dashboard.chips.noneOverdue')}
      subTone={overdue > 0 ? 'red' : undefined}
      onClick={() => navigate('/invoices')}
    />
  );
}

export function TillWidget({ opts }) {
  const { t } = useTranslation();
  const period = opts?.period === 'week' ? 'week' : 'day';
  const { data } = useSource(dataSources.cashRegister(period));
  const receipts = (data?.cash_count ?? 0) + (data?.card_count ?? 0) + (data?.bank_transfer_count ?? 0);
  useHeader({ chip: t(period === 'week' ? 'dashboard.chips.thisWeek' : 'dashboard.chips.today'), link: { labelKey: 'dashboard.actions.openReports', to: '/reports' }, empty: Boolean(data) && receipts === 0 && (data?.refund_count ?? 0) === 0 }, [data, period, receipts]);
  return (
    <BreakdownTile
      icon={PointOfSaleIcon}
      title={t('dashboard.widgets.till.title')}
      value={fmtEur2(data?.total_sales ?? 0)}
      unit={t('dashboard.chips.inclVat')}
      chips={[
        { n: receipts, label: t('dashboard.chips.receipts') },
        { n: data?.parts_count ?? 0, label: t('dashboard.chips.parts') },
        { n: data?.refund_count ?? 0, label: t('dashboard.chips.refunds') },
      ]}
      rows={[
        { icon: PaymentsIcon, label: t('dashboard.chips.cash'), value: fmtEur2(data?.cash_total ?? 0) },
        { icon: CreditCardIcon, label: t('dashboard.chips.card'), value: fmtEur2(data?.card_total ?? 0) },
        { icon: AccountBalanceIcon, label: t('dashboard.chips.bank'), value: fmtEur2(data?.bank_transfer_total ?? 0) },
      ]}
    />
  );
}

export function Revenue6Chart() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.stats());
  const rows = (data?.revenue_chart || []).map((row) => ({ label: row.month, value: row.revenue }));
  useHeader({ chip: '6 kk', link: { labelKey: 'dashboard.actions.openReports', to: '/reports' }, empty: Boolean(data) && rows.every((row) => !row.value) }, [data, rows.length]);
  return <ChartBody kind="line" data={rows} tickFormatter={(value) => `${value / 1000}k`} valueFormatter={fmtEur0} tooltipLabel={t('Revenue')} />;
}

export function WeeklyChart() {
  const { t } = useTranslation();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.stats());
  const rows = (data?.weekly_maintenances_chart || []).map((row) => ({ label: row.day, value: row.jobs }));
  useHeader({ chip: t('dashboard.chips.thisWeek'), link: { labelKey: 'dashboard.actions.showAll', onClick: () => navigateToMaintenancesWithFilters({ dateRangeType: 'this_week' }) }, empty: Boolean(data) && rows.every((row) => !row.value) }, [data, rows.length]);
  return <ChartBody kind="bar" data={rows} valueFormatter={(value) => fmtNumber(value)} tooltipLabel={t('dashboard.chips.workOrders')} />;
}

export function DueWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useMemo(() => ({ status: 'sent', due_date_from: todayIso(), due_date_to: addDaysIso(7), ordering: 'due_date', page_size: 10 }), []);
  const { data } = useSource(dataSources.invoices(params, 'due'));
  const rows = listOf(data).map((invoice) => {
    const days = daysUntil(invoice.due_date);
    return {
      key: invoice.id,
      icon: ReceiptIcon,
      primary: invoice.customer_name || invoice.customer?.name || '',
      secondary: `${t('Invoice')} ${invoice.invoice_number}`,
      right: fmtEur2(invoice.total_incl_vat),
      chip: `${days} ${t('dashboard.chips.daysShort')}`,
      chipTone: days <= 1 ? 'red' : 'neutral',
      onClick: () => navigate(`/invoices/${invoice.id}`),
    };
  });
  useHeader({ link: { labelKey: 'dashboard.actions.openInvoices', to: '/invoices' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} onMore={() => navigate('/invoices')} />;
}

export function EinvoiceWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useMemo(() => ({ maventa_status: 'failed', page_size: 10 }), []);
  const { data } = useSource(dataSources.invoices(params, 'einvoice'));
  const rows = listOf(data).map((invoice) => ({
    key: invoice.id,
    icon: ErrorIcon,
    iconColor: '#c53030',
    primary: `${t('Invoice')} ${invoice.invoice_number} · ${invoice.customer_name || ''}`,
    secondary: invoice.maventa_error_message || '',
    action: { label: t('dashboard.actions.open'), onClick: () => navigate(`/invoices/${invoice.id}`) },
    onClick: () => navigate(`/invoices/${invoice.id}`),
  }));
  useHeader({ titleTone: rows.length ? 'red' : undefined, count: rows.length || null, link: { labelKey: 'dashboard.actions.openInvoices', to: '/invoices' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} onMore={() => navigate('/invoices')} />;
}

export function KpisWidget({ opts }) {
  const { t } = useTranslation();
  const period = opts?.period || 'month';
  const { data } = useSource(dataSources.summary(period));
  useHeader({ chip: t(`dashboard.chips.period.${period}`, { defaultValue: period }), link: { labelKey: 'dashboard.actions.openReports', to: '/reports' } }, [period]);
  const items = data
    ? [
        { label: `${t('Revenue')} · ${t('dashboard.chips.exclVat')}`, value: fmtEur0(data.revenue?.value ?? 0), change: changeOf(data.revenue) },
        { label: t('dashboard.chips.workOrders'), value: fmtNumber(data.work_orders?.value ?? 0), change: changeOf(data.work_orders) },
        { label: t('dashboard.chips.activeCustomers'), value: fmtNumber(data.active_customers?.value ?? 0), change: changeOf(data.active_customers) },
        { label: t('dashboard.chips.averageInvoice'), value: fmtEur0(data.average_invoice?.value ?? 0), change: changeOf(data.average_invoice) },
      ]
    : [];
  return <KpiGroupBody items={items} />;
}

export function TopCustomersWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.summary('month'));
  const rows = (data?.top_customers || []).slice(0, 5).map((customer, index) => ({
    key: customer.id || index,
    avatar: initials(customer.name),
    avatarIdx: index,
    primary: customer.name,
    secondary: t('dashboard.chips.workOrdersCount', { count: customer.orders ?? 0 }),
    right: fmtEur0(customer.revenue),
    onClick: customer.id ? () => navigate(`/customers?customer=${customer.id}`) : undefined,
  }));
  useHeader({ chip: t('dashboard.chips.thisMonth'), link: { labelKey: 'dashboard.actions.openReports', to: '/reports' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} />;
}

export function MechSalesWidget() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.performance('month'));
  const rows = (data?.employees || [])
    .slice()
    .sort((a, b) => (Number(b.total_revenue) || 0) - (Number(a.total_revenue) || 0))
    .map((employee, index) => ({
      key: employee.id || index,
      avatar: initials(employee.name),
      avatarIdx: index,
      primary: employee.name,
      secondary: `${t('dashboard.chips.labour')} ${fmtEur0(employee.labor_revenue)} · ${t('dashboard.chips.parts')} ${fmtEur0(employee.parts_revenue)}`,
      right: fmtEur0(employee.total_revenue),
    }));
  useHeader({ chip: t('dashboard.chips.thisMonth'), link: { labelKey: 'dashboard.actions.openReports', to: '/reports' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} />;
}

export function MarginTile() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.profitability('month'));
  const value = data?.avg_margin_percent;
  return <KpiTile icon={TrendingUpIcon} value={value != null ? `${Math.round(value)} %` : '–'} label={t('dashboard.widgets.margin.title')} sub={t('dashboard.chips.thisMonth')} />;
}

export function TurnaroundTile() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.workOrdersReport('month'));
  const value = data?.avg_turnaround_days;
  return <KpiTile icon={TimelapseIcon} value={value != null ? `${Number(value).toFixed(1).replace('.', ',')} ${t('dashboard.chips.daysShort')}` : '–'} label={t('dashboard.widgets.turnaround.title')} sub={t('dashboard.chips.thisMonth')} />;
}
